import { AnimatePresence } from 'framer-motion';
import React, { FC, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../assets/icons/index';
import { AnimatedNumber } from '../../components/animated-number/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { CrossfadePanel, StaggerFadeIn, StaggerItem } from '../../components/motion/index';
import { Tooltip } from '../../components/tooltip/index';
import { sideBarContext } from '../../context/side-bar-context';
import { Company, SnapshotHooks, useScreenSize } from '../../hooks/index';
import { PageRoute } from '../../types/page.types';
import { CapTableGraphWrapper } from '../cap-table/graphs/index';
import { OverAllStatus } from '../cap-table/over-all-status';
import { GraphVariant } from '../cap-table/variables';
import {
  EmptyGraph,
  EventGraph,
  valuationChartUsesShareCount,
} from '../events/desktop/graphs-desk/index';
import { useCompanyIncorporationEvent } from '../events/hooks/use-company-incorporation-event.hook';
import { GraphVariant as EventGraphVariant } from '../events/variables';
import { DashboardPlaceholder } from './dashboard-loading-placeholders';
import { useDashboardSecondaryQueriesEnabled } from './hooks/use-dashboard-secondary-queries-enabled.hook';
import { PoolGraph } from './pool-graph';
import { PoolGraphVariant } from './variables';

const Dashboard: FC = () => {
  const [selectedGraphVariant, setGraphVariant] = useState(GraphVariant.OWNERSHIP);
  const { selectedCompanyId } = Company.useSelected();
  const secondaryQueriesEnabled = useDashboardSecondaryQueriesEnabled(selectedCompanyId);
  // Also the valuation graph's Fully Diluted point: it must exclude phantom grants like the stored
  // cap-table rows behind every valuation point, or the line steps at the end with no event.
  const {
    snapshot,
    isLoading: isCurrentSnapshotLoading,
    isSuccess: isLoadingFinished,
  } = SnapshotHooks.useCurrent({
    companyId: selectedCompanyId,
    capTableMode: true,
  });
  const { snapshots, isLoading } = SnapshotHooks.useSnapshots({
    companyId: selectedCompanyId,
    capTableMode: true,
    enabled: secondaryQueriesEnabled,
  });
  const { companyIncorporationEvent, isLoading: isIncorporationLoading } =
    useCompanyIncorporationEvent({
      companyId: selectedCompanyId,
      enabled: secondaryQueriesEnabled,
    });
  const valuationUsesShareCount = valuationChartUsesShareCount(snapshots);
  const hasValuationHistoryGraph =
    (snapshots.length > 0 || Boolean(companyIncorporationEvent)) &&
    !isLoading &&
    !isCurrentSnapshotLoading &&
    !isIncorporationLoading;
  const { width } = useScreenSize();
  const { isOpen: isSideBarOpen } = useContext(sideBarContext);
  const navigate = useNavigate();

  const toggleGraphVariant = () => {
    setGraphVariant((prevVariant) =>
      prevVariant === GraphVariant.OWNERSHIP ? GraphVariant.VOTING_RIGHTS : GraphVariant.OWNERSHIP,
    );
  };

  const stakeholdersWithDilutedHoldings = useMemo(
    () => snapshot?.stats?.stakeholders.filter((stakeholder) => stakeholder.diluted > 0) ?? [],
    [snapshot?.stats?.stakeholders],
  );

  const top5Stakeholders = useMemo(
    () =>
      [...stakeholdersWithDilutedHoldings]
        .sort((a, b) => b.issuedPercentage - a.issuedPercentage)
        .slice(0, 5)
        .map(({ name, issuedPercentage, dilutedPercentage, votingPercentage }) => ({
          name,
          issuedPercentage: issuedPercentage * 100,
          dilutedPercentage: dilutedPercentage * 100,
          votingPercentage: votingPercentage * 100,
        })),
    [stakeholdersWithDilutedHoldings],
  );

  const isStakeholdersSnapshotEmpty = stakeholdersWithDilutedHoldings.length === 0;

  const isDesktop = width >= 1344;
  const isTablet = width >= (isSideBarOpen ? 1114 : 1024) && width < 1344;

  const showPlaceholder = isCurrentSnapshotLoading && !snapshot;

  return (
    <AnimatePresence mode="wait">
      {showPlaceholder ? (
        <CrossfadePanel
          className="h-fit w-full"
          key="dashboard-loading"
          panelKey="dashboard-loading"
        >
          <DashboardPlaceholder isDesktop={isDesktop} isTablet={isTablet} />
        </CrossfadePanel>
      ) : (
        <CrossfadePanel
          className="flex h-fit w-full flex-col gap-4 overflow-hidden lg:pl-1"
          key="dashboard-content"
          panelKey="dashboard-content"
        >
          <div className="flex h-max w-full items-stretch gap-4 max-lg:flex-col">
            <OverAllStatus
              isLoadingFinished={isLoadingFinished}
              shouldNavigate
              snapshot={snapshot}
              wrapperClassName={isTablet ? 'w-[40%]' : 'w-[100%]'}
            />
            <CapTableGraphWrapper
              isDesktop={isDesktop}
              isLoadingFinished={isLoadingFinished}
              isTablet={isTablet}
              selectedGraphVariant={selectedGraphVariant}
              snapshot={snapshot}
              toggleGraphVariant={toggleGraphVariant}
              wrapperClassName={isTablet ? 'w-[60%]' : 'w-[100%]'}
            />
          </div>
          <div className="flex h-max w-full justify-center gap-4 max-lg:flex-col">
            <div
              className={twMerge(
                'flex min-h-full flex-col overflow-x-auto rounded-lg bg-gray-50 p-1',
                isDesktop || isTablet ? 'w-[50%]' : 'w-[100%]',
              )}
            >
              <div className="flex w-full min-w-[400px] justify-between gap-4 px-4 py-3">
                <span className="sticky left-4 w-fit text-left text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
                  <AppFormattedMessage id="TOP_5_STAKEHOLDERS" />
                </span>
                <ChevronDownIcon
                  className="sticky right-4 w-fit -rotate-90 transform cursor-pointer"
                  onClick={() => navigate(PageRoute.STAKEHOLDERS)}
                />
              </div>
              <div
                className={twMerge(
                  'centered relative flex h-full w-full min-w-[400px] flex-col rounded-md bg-white p-2 shadow-sm lg:px-6',
                )}
              >
                {isStakeholdersSnapshotEmpty && isLoadingFinished && (
                  <div className="centered absolute z-1 flex flex-col gap-3">
                    <span className="text-label-md font-medium text-gray-500">
                      <AppFormattedMessage id="STAKEHOLDERS_SNAPSHOT_WILL_BE_DISPLAYED_HERE" />
                    </span>
                    <Button
                      className="border border-gray-300 bg-gray-25 px-4 py-[10px] text-sm font-[450] text-gray-700"
                      data-permission-required
                      onClick={() => navigate(PageRoute.STAKEHOLDERS)}
                      styleType="DEFAULT_ROUNDED"
                    >
                      <AppFormattedMessage id="ADD_STAKEHOLDERS" />
                    </Button>
                  </div>
                )}
                <div
                  className={twMerge(
                    'flex h-full w-full flex-col',
                    isStakeholdersSnapshotEmpty && 'select-none blur-md',
                  )}
                >
                  <div className="flex min-h-[34px] w-full justify-between gap-3 text-label-md font-[500] text-gray-700">
                    <span className="w-full max-w-[160px] whitespace-nowrap">
                      <AppFormattedMessage id="NAME" />
                    </span>
                    <span className="flex w-full max-w-[60px] whitespace-nowrap">
                      <AppFormattedMessage id="ISSUED_PERCENTAGE" />
                    </span>
                    <span className="flex w-full max-w-[100px] whitespace-nowrap">
                      <AppFormattedMessage id="DILUTED_PERCENTAGE" />
                    </span>
                    <span className="flex w-full max-w-[60px] whitespace-nowrap">
                      <AppFormattedMessage id="VOTING_PERCENTAGE" />
                    </span>
                  </div>
                  <StaggerFadeIn className="flex w-full flex-col" key={selectedCompanyId}>
                    {top5Stakeholders.map((stakeholder, index) => (
                      <StaggerItem
                        className="flex h-[46px] min-h-[33px] w-full justify-between gap-3 text-sm font-[450] text-gray-600"
                        key={`${stakeholder.name}_${index}`}
                      >
                        <Tooltip content={stakeholder.name}>
                          <span className="block w-full max-w-[160px] truncate text-xs text-gray-700">
                            {stakeholder.name}
                          </span>
                        </Tooltip>
                        <span className="flex w-full max-w-[60px]">
                          <AnimatedNumber
                            maximumFractionDigits={2}
                            minimumFractionDigits={2}
                            value={stakeholder.issuedPercentage}
                          />
                        </span>
                        <span className="flex w-full max-w-[100px]">
                          <AnimatedNumber
                            maximumFractionDigits={2}
                            minimumFractionDigits={2}
                            value={stakeholder.dilutedPercentage}
                          />
                        </span>
                        <span className="flex w-full max-w-[60px]">
                          <AnimatedNumber
                            maximumFractionDigits={2}
                            minimumFractionDigits={2}
                            value={stakeholder.votingPercentage}
                          />
                        </span>
                      </StaggerItem>
                    ))}
                  </StaggerFadeIn>
                </div>
              </div>
            </div>
            <div
              className={twMerge(
                'flex min-h-full flex-col items-center overflow-x-hidden rounded-lg bg-gray-50 p-1',
                isDesktop || isTablet ? 'w-[50%]' : 'w-[100%]',
              )}
            >
              <div className="flex w-full items-start justify-between gap-4 px-4 py-3">
                <span className="w-full text-left text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
                  <AppFormattedMessage id="POOL_ALLOCATION" />
                </span>
                <ChevronDownIcon
                  className="-rotate-90 transform cursor-pointer"
                  onClick={() => {
                    navigate(PageRoute.POOL_PLANS);
                  }}
                />
              </div>
              <div
                className={twMerge(
                  'relative flex h-[280px] w-full gap-4 rounded-md bg-white shadow-sm',
                  isDesktop ? 'p-6' : isTablet ? 'p-5' : 'h-full flex-col gap-6 p-2',
                )}
              >
                <PoolGraph
                  isDesktop={isDesktop}
                  isLoadingFinished={isLoadingFinished}
                  isTablet={isTablet}
                  snapshot={snapshot}
                  variant={PoolGraphVariant.GRANTED}
                />
              </div>
            </div>
          </div>
          <div className="h-max w-full max-lg:hidden">
            <div className="flex min-h-full flex-col items-center rounded-lg bg-gray-50 p-1">
              <div className="flex w-full items-start justify-between gap-4 px-4 py-3">
                <span className="w-full text-left text-[10px] font-bold uppercase leading-4 tracking-[1px] text-gray-700">
                  <AppFormattedMessage id="VALUATION_HISTORY" />
                </span>
                <ChevronDownIcon
                  className="-rotate-90 transform cursor-pointer"
                  onClick={() => {
                    navigate(PageRoute.EVENTS);
                  }}
                />
              </div>
              <div className="relative flex h-full w-full rounded-md bg-white p-6 pl-2 shadow-sm">
                <span className="-translate-x-1/6 absolute left-[90px] translate-y-[300%] whitespace-nowrap text-label-sm font-medium text-gray-500">
                  {valuationUsesShareCount ? (
                    <AppFormattedMessage id="NUMBER_OF_SHARES_IN_THOUSANDS" />
                  ) : (
                    <AppFormattedMessage id="CURRENCY_USD" />
                  )}
                </span>
                <div className="h-[344px] w-full">
                  <AnimatePresence mode="wait">
                    {hasValuationHistoryGraph ? (
                      <CrossfadePanel
                        className="h-full w-full"
                        key="valuation-graph"
                        panelKey="valuation-graph"
                      >
                        <EventGraph
                          companyIncorporationEvent={companyIncorporationEvent ?? null}
                          currentSnapshot={snapshot}
                          snapshots={snapshots}
                          variant={EventGraphVariant.VALUATION}
                        />
                      </CrossfadePanel>
                    ) : (
                      <CrossfadePanel
                        className="h-full w-full"
                        key="valuation-empty"
                        panelKey="valuation-empty"
                      >
                        <EmptyGraph
                          isLoadingFinished={isLoadingFinished}
                          variant={EventGraphVariant.VALUATION}
                        />
                      </CrossfadePanel>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </div>
        </CrossfadePanel>
      )}
    </AnimatePresence>
  );
};

export default Dashboard;
